import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { CommentableType, CommentTag } from "@shared/types";
import { Inline } from "@ds/layout/Inline";
import { useCurrentEmployee } from "@ds/actions/useCurrentEmployee";
import { useCommentActions } from "./useCommentActions";
import { CommentEditor } from "./CommentEditor";
import { CommentAvatar } from "./CommentAvatar";

interface CommentComposerProps {
  commentableType: CommentableType;
  commentableId: number;
  /** Show tag picker. Default true. Set to false in narrow popover contexts. */
  full?: boolean;
  /** Default tag preset (e.g. for cancel modals). */
  defaultTag?: CommentTag;
  /** Default pin state. No UI toggle — composer creates pinned comments invisibly. */
  defaultPinned?: boolean;
  /** Called after successful create. */
  onCreated?: () => void;
  /** Called on Escape. */
  onCancel?: () => void;
  autoFocus?: boolean;
}

export const CommentComposer: React.FC<CommentComposerProps> = ({
  commentableType,
  commentableId,
  full = true,
  defaultTag,
  defaultPinned = false,
  onCreated,
  onCancel,
  autoFocus = false,
}) => {
  const { t } = useTranslation();
  const actions = useCommentActions();
  const { employee } = useCurrentEmployee();

  const [content, setContent] = useState("");
  const [tag, setTag] = useState<CommentTag | null>(defaultTag ?? null);
  const [submitting, setSubmitting] = useState(false);

  const initials = (employee?.full_name ?? "?")[0]?.toUpperCase() ?? "?";

  const reset = () => {
    setContent("");
    setTag(defaultTag ?? null);
  };

  const handleSubmit = () => {
    const trimmed = content.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    actions.create({
      commentableType,
      commentableId,
      content: trimmed,
      tag,
      isPinned: defaultPinned,
      onSuccess: () => {
        setSubmitting(false);
        reset();
        onCreated?.();
      },
      onError: () => setSubmitting(false),
    });
  };

  const handleCancel = onCancel ?? (() => reset());

  return (
    <Inline gap="sm" align="start">
      <CommentAvatar photo={employee?.thumbnail_photo_url} initials={initials} />
      <CommentEditor
        content={content}
        onChange={setContent}
        tag={tag}
        onTagChange={setTag}
        onSubmit={handleSubmit}
        onCancel={onCancel ? handleCancel : undefined}
        submitLabel={t("comments.actions.add_comment")}
        hideTag={!full}
        autoFocus={autoFocus}
        disabled={submitting}
      />
    </Inline>
  );
};
